import type { ReactNode } from "react";
import { NavLink } from "react-router-dom";
import { useTranslate } from "@/lib/i18n";
import { NavIcon } from "./navIcons";
import { navRowClass, type ManageNavGroup, type ManageNavItem } from "./nav";

/**
 * The order the sections appear in — by JOB since the 2026-10-14 round table
 * (D152): the floor, then the money (the drawer, its log, the day's تقفيلة, the
 * give-backs and the purchase tax — one close, top to bottom), then the menu,
 * the stock, the analysis, and the shop's own settings last.
 */
export const GROUP_ORDER: ManageNavGroup[] = [
  "service",
  "money",
  "menu",
  "inventory",
  "reports",
  "store",
];

/**
 * The money group's own order (D152): the floor manager's close, top to
 * bottom — count the drawer, read the log, read the day, then the give-backs,
 * then what each person took off a ticket, then the purchase tax. Rows not
 * named keep `rows.ts` order after these.
 */
export const MONEY_ORDER: readonly ManageNavItem["rowKey"][] = [
  "shift",
  "shiftHistory",
  "dayClose",
  "reportRefunds",
  "reportStaff",
  "reportInputVat",
];

/** A group's rows in the order the nav draws them. */
export function orderRowsInGroup(
  group: ManageNavGroup,
  rows: ManageNavItem[],
): ManageNavItem[] {
  if (group !== "money") return rows;
  const rank = (item: ManageNavItem) => {
    const at = MONEY_ORDER.indexOf(item.rowKey);
    return at === -1 ? MONEY_ORDER.length : at;
  };
  return [...rows].sort((a, b) => rank(a) - rank(b));
}

/** The nav list flattened into group order. The rail picks its slots from this. */
export function orderByGroup(items: ManageNavItem[]): ManageNavItem[] {
  return GROUP_ORDER.flatMap((group) =>
    orderRowsInGroup(
      group,
      items.filter((item) => item.group === group),
    ),
  );
}

/**
 * The navigation rows, grouped and iconed, shared by the sidebar and the drawer.
 *
 * One component rather than the same `items.map(...)` written twice: the two
 * surfaces had already drifted once (the drawer and the sidebar each styled
 * their own rows), and a grouped list is more markup to keep in sync than a
 * flat one.
 *
 * A section heading only renders when that section HAS rows. Permission
 * filtering happens upstream, so a cashier who can reach two screens sees two
 * rows under one heading rather than three empty headings.
 */
export function ManageNavList({
  items,
  onNavigate,
  iconFor,
}: {
  items: ManageNavItem[];
  /** Called after a row is followed — the drawer closes itself on navigate. */
  onNavigate?: () => void;
  /** A row's glyph, when the caller draws another set (PAPER's line icons). */
  iconFor?: (item: ManageNavItem) => ReactNode;
}) {
  const t = useTranslate();
  const label: Record<ManageNavGroup, string> = {
    service: t((m) => m.nav.groupService),
    money: t((m) => m.nav.groupMoney),
    menu: t((m) => m.nav.groupMenu),
    inventory: t((m) => m.nav.groupInventory),
    store: t((m) => m.nav.groupStore),
    reports: t((m) => m.nav.groupReports),
  };

  return (
    <>
      {GROUP_ORDER.map((group) => {
        const rows = orderRowsInGroup(
          group,
          items.filter((item) => item.group === group),
        );
        if (rows.length === 0) return null;
        return (
          <div key={group} className="flex flex-col gap-1">
            <p className="px-3 pb-1 pt-3 text-[11px] font-semibold tracking-[.08em] text-[color:var(--sidebar-ink-muted)]">
              {label[group]}
            </p>
            {rows.map((item) => {
              const Icon = NavIcon[item.icon];
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  onClick={onNavigate}
                  className={({ isActive }) => `${navRowClass(isActive)} gap-2.5`}
                >
                  <span aria-hidden="true" className="inline-flex shrink-0">
                    {iconFor ? iconFor(item) : <Icon />}
                  </span>
                  {item.label}
                </NavLink>
              );
            })}
          </div>
        );
      })}
    </>
  );
}
